import type {
  BodyFontManifestEntry,
  FontManifest,
  FontSystemIssue,
  HeadingFontManifestEntry,
  InvalidFontManifestJsonIssue,
  InvalidFontManifestSchemaIssue,
  MissingFontManifestIssue,
} from "./types"
import {
  FONT_MANIFEST_PATH,
  REQUIRED_BODY_CSS_VARIABLE,
  REQUIRED_HEADING_CSS_VARIABLE,
} from "./constants"

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0

const isNonEmptyNumberArray = (value: unknown): value is readonly [number, ...number[]] =>
  Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === "number" && Number.isFinite(item))

const isNonEmptyStringArray = (value: unknown): value is readonly [string, ...string[]] =>
  Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === "string" && item.trim().length > 0)

const missingFontManifest = (): MissingFontManifestIssue => ({
  _tag: "MissingFontManifest",
  path: FONT_MANIFEST_PATH,
})

const invalidFontManifestJson = (): InvalidFontManifestJsonIssue => ({
  _tag: "InvalidFontManifestJson",
  path: FONT_MANIFEST_PATH,
  message: "File is not valid JSON.",
})

const invalidFontManifestSchema = (
  field: string,
  message: string,
): InvalidFontManifestSchemaIssue => ({
  _tag: "InvalidFontManifestSchema",
  path: FONT_MANIFEST_PATH,
  field,
  message,
})

interface FontManifestDecodeResult {
  readonly manifest: FontManifest | null
  readonly issues: readonly FontSystemIssue[]
}

interface BodyEntryDecodeResult {
  readonly entry: BodyFontManifestEntry | null
  readonly issues: readonly InvalidFontManifestSchemaIssue[]
}

interface HeadingEntryDecodeResult {
  readonly entry: HeadingFontManifestEntry | null
  readonly issues: readonly InvalidFontManifestSchemaIssue[]
}

const decodeBodyEntry = (value: unknown): BodyEntryDecodeResult => {
  const issues: InvalidFontManifestSchemaIssue[] = []

  if (!isRecord(value)) {
    issues.push(invalidFontManifestSchema("body", "must be an object."))
    return { entry: null, issues }
  }

  const name = value.name
  const weights = value.weights
  const fallback = value.fallback
  const cssVariable = value.cssVariable

  if (!isNonEmptyString(name)) {
    issues.push(invalidFontManifestSchema("body.name", "must be a non-empty string."))
  }
  if (!isNonEmptyNumberArray(weights)) {
    issues.push(invalidFontManifestSchema("body.weights", "must be a non-empty array of numbers."))
  }
  if (!isNonEmptyStringArray(fallback)) {
    issues.push(invalidFontManifestSchema("body.fallback", "must be a non-empty array of strings."))
  }
  if (cssVariable !== REQUIRED_BODY_CSS_VARIABLE) {
    issues.push(invalidFontManifestSchema("body.cssVariable", `must be "${REQUIRED_BODY_CSS_VARIABLE}".`))
  }

  if (issues.length > 0 || !isNonEmptyString(name) || !isNonEmptyNumberArray(weights) || !isNonEmptyStringArray(fallback)) {
    return { entry: null, issues }
  }

  return {
    entry: {
      name,
      weights,
      fallback,
      cssVariable: REQUIRED_BODY_CSS_VARIABLE,
    },
    issues,
  }
}

const decodeHeadingEntry = (value: unknown): HeadingEntryDecodeResult => {
  const issues: InvalidFontManifestSchemaIssue[] = []

  if (!isRecord(value)) {
    issues.push(invalidFontManifestSchema("heading", "must be an object."))
    return { entry: null, issues }
  }

  const name = value.name
  const weights = value.weights
  const fallback = value.fallback
  const cssVariable = value.cssVariable

  if (!isNonEmptyString(name)) {
    issues.push(invalidFontManifestSchema("heading.name", "must be a non-empty string."))
  }
  if (!isNonEmptyNumberArray(weights)) {
    issues.push(invalidFontManifestSchema("heading.weights", "must be a non-empty array of numbers."))
  }
  if (!isNonEmptyStringArray(fallback)) {
    issues.push(invalidFontManifestSchema("heading.fallback", "must be a non-empty array of strings."))
  }
  if (cssVariable !== REQUIRED_HEADING_CSS_VARIABLE) {
    issues.push(invalidFontManifestSchema("heading.cssVariable", `must be "${REQUIRED_HEADING_CSS_VARIABLE}".`))
  }

  if (issues.length > 0 || !isNonEmptyString(name) || !isNonEmptyNumberArray(weights) || !isNonEmptyStringArray(fallback)) {
    return { entry: null, issues }
  }

  return {
    entry: {
      name,
      weights,
      fallback,
      cssVariable: REQUIRED_HEADING_CSS_VARIABLE,
    },
    issues,
  }
}

export const validateFontManifest = (
  input: string | null | undefined,
): FontManifestDecodeResult => {
  if (input == null) {
    return { manifest: null, issues: [missingFontManifest()] }
  }

  let raw: unknown
  try {
    raw = JSON.parse(input)
  } catch {
    return { manifest: null, issues: [invalidFontManifestJson()] }
  }

  if (!isRecord(raw)) {
    return {
      manifest: null,
      issues: [invalidFontManifestSchema("root", "must be a JSON object.")],
    }
  }

  const issues: InvalidFontManifestSchemaIssue[] = []
  const version = raw.version
  const provider = raw.provider

  if (version !== 1) {
    issues.push(invalidFontManifestSchema("version", "must be 1."))
  }
  if (provider !== "google") {
    issues.push(invalidFontManifestSchema("provider", 'must be "google".'))
  }

  const body = decodeBodyEntry(raw.body)
  const heading = decodeHeadingEntry(raw.heading)

  issues.push(...body.issues)
  issues.push(...heading.issues)

  if (issues.length > 0 || body.entry === null || heading.entry === null) {
    return { manifest: null, issues }
  }

  return {
    manifest: {
      version: 1,
      provider: "google",
      body: body.entry,
      heading: heading.entry,
    },
    issues: [],
  }
}
